import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { DocumentNode } from 'graphql';
import buildQueryKey from '../../utils/buildQueryKey';
import fetchEdge from '../../utils/fetchEdge';

//
// The page variable is handled internally via queryFunctionContext,
// so we make it optional here.
interface useEdgeInfiniteQueryOptions<T> extends Dict {
  variables?: Optional<T, 'page'>;
}

/**
 *
 * @param query
 * @param options
 */
const useEdgeInfiniteQuery = <
  TQueryFnData extends Dict = Dict,
  TVariable extends Dict = Dict,
  TData = TQueryFnData,
>(
  query: DocumentNode,
  options: useEdgeInfiniteQueryOptions<TVariable> = {},
) => {
  const {
    variables = {},
    context = {},
    formatContext,
    ...queryOptions
  } = options;

  // useInfiniteQuery has queryFunctionContext: queryKey and pageParam
  const queryFn = useCallback(
    (queryFunctionContext) => {
      if (!formatContext) {
        const nextIndex =
          queryFunctionContext.pageParam === 2
            ? queryOptions.numberOfItems / 10 + 1
            : queryFunctionContext.pageParam;

        const index = queryFunctionContext.pageParam ? nextIndex : 1;

        if (typeof index === 'number') {
          queryFunctionContext.queryKey[1].variables.page = {
            index,
            limit: index === 1 ? queryOptions.numberOfItems : 10,
          };
          queryFunctionContext.queryKey[1].variables.after = undefined;
        }

        if (typeof index === 'string') {
          queryFunctionContext.queryKey[1].variables.page = undefined;
          queryFunctionContext.queryKey[1].variables.after =
            queryFunctionContext.pageParam;
        }
      }

      const formattedFunctionContext = formatContext
        ? formatContext(queryFunctionContext)
        : queryFunctionContext;

      return fetchEdge<TQueryFnData>(formattedFunctionContext, query, {
        context,
      });
    },
    [context, query],
  );

  const {
    data,
    isLoading,
    isInitialLoading,
    isFetchingNextPage,
    isSuccess,
    refetch,
    fetchNextPage,
    error,
  } = useInfiniteQuery<TQueryFnData, Error, TData>(
    buildQueryKey({ query, variables }),
    queryFn,
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      ...queryOptions,
    },
  );

  return {
    data,
    isLoading,
    isInitialLoading,
    isSuccess,
    error,
    isFetchingNextPage,
    refetch,
    fetchNextPage,
  };
};

export default useEdgeInfiniteQuery;
